/**
 * The Files browser toolbar — New folder, Upload, and the upload progress list.
 *
 * New folder opens an inline input row at the top of the list rather than a
 * window.prompt, so the user sees where the folder will land and can cancel by
 * clicking away. The actual input row lives in the list component; the toolbar
 * only toggles the intent.
 *
 * Upload progress is per-file (name + state + error) instead of the old single
 * "Working in …" line, so a multi-file upload does not collapse every failure
 * into the first one. Cancel-per-file is a deliberate follow-up, not v1.
 */

import type { ChangeEvent, ReactElement } from 'react';
import './FilesToolbarComponent.css';

export type UploadState = 'uploading' | 'done' | 'error';

export interface UploadProgressItem {
  id: string;
  name: string;
  state: UploadState;
  error?: string;
}

export interface FilesToolbarComponentProps {
  /** Whether the current directory accepts writes (writable mount, not synced). */
  canWrite: boolean;
  /** Whether a new-folder input row is currently showing. */
  isCreatingFolder: boolean;
  /** Whether the up-one-level button is available (false at a mount root). */
  canGoUp: boolean;
  onGoUp: () => void;
  onStartNewFolder: () => void;
  onCancelNewFolder: () => void;
  onPickFiles: (files: FileList) => void;
  uploads: UploadProgressItem[];
}

export function FilesToolbarComponent({
  canWrite,
  isCreatingFolder,
  canGoUp,
  onGoUp,
  onStartNewFolder,
  onCancelNewFolder,
  onPickFiles,
  uploads,
}: FilesToolbarComponentProps): ReactElement {
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) onPickFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div className="files-toolbar">
      <div className="files-toolbar-actions">
        <button
          type="button"
          className="files-toolbar-button files-toolbar-button--icon"
          onClick={onGoUp}
          disabled={!canGoUp}
          title="Go up one level"
          aria-label="Go up one level"
        >
          ‹
        </button>
        {canWrite && (
          <>
            <button
              type="button"
              className="files-toolbar-button"
              onClick={isCreatingFolder ? onCancelNewFolder : onStartNewFolder}
            >
              {isCreatingFolder ? 'Cancel folder' : 'New folder'}
            </button>
            <label className="files-toolbar-button files-toolbar-button--upload">
              Upload
              <input type="file" multiple onChange={handleFileChange} hidden />
            </label>
          </>
        )}
        {!canWrite && <span className="files-toolbar-readonly">This folder is read-only</span>}
      </div>

      {uploads.length > 0 && (
        <ul className="files-toolbar-uploads" role="status" aria-live="polite">
          {uploads.map((u) => (
            <li key={u.id} className={`files-toolbar-upload files-toolbar-upload--${u.state}`}>
              <span className="files-toolbar-upload-name" title={u.name}>{u.name}</span>
              {u.state === 'uploading' && <span className="files-toolbar-upload-state">Uploading…</span>}
              {u.state === 'done' && <span className="files-toolbar-upload-state">Done</span>}
              {u.state === 'error' && <span className="files-toolbar-upload-state files-toolbar-upload-state--error">{u.error ?? 'Failed'}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
